import {
  ButtonLink,
  Column,
  Columns,
  Divider,
  IconAdd,
  Inline,
  Stack,
} from 'braid-design-system';
import { useNavigate, useParams } from 'react-router';

import {
  NewSubscriptionPanel,
  NewSubscriptionPanelProvider,
} from './components/NewSubscriptionPanel/NewSubscriptionPanel.tsx';
import { SubscriptionListPanel } from './components/SubscriptionListPanel/SubscriptionListPanel.tsx';
import {
  HirerPicker,
  useHirerState,
} from './components/SubscriptionListPanel/components/HirerPicker/HirerPicker.tsx';
import {
  ScopePicker,
  useScopeState,
} from './components/SubscriptionListPanel/components/ScopePicker/ScopePicker.tsx';
import {
  ViewSubscriptionPanel,
  ViewSubscriptionPanelProvider,
} from './components/ViewSubscriptionPanel/ViewSubscriptionPanel.tsx';

import { BasePage } from '#src/components/BasePage/BasePage.tsx';
import type { Breadcrumb } from '#src/components/Breadcrumbs/Breadcrumbs.tsx';
import {
  EventTypePicker,
  useEventTypeState,
} from '#src/components/EventTypePicker/EventTypePicker.tsx';
import { ResponsiveDrawerOrInline } from '#src/components/ResponsiveDrawerOrInline/ResponsiveDrawerOrInline.tsx';
import { usePermissions } from '#src/hooks/auth.tsx';

const SubscriptionDetails = ({
  subscriptionId,
  onClose,
}: {
  subscriptionId: string;
  onClose: () => void;
}) => {
  if (subscriptionId === 'new') {
    return (
      <NewSubscriptionPanelProvider>
        <ResponsiveDrawerOrInline
          onClose={onClose}
          title="Create new subscription"
          inlineAt="desktop"
        >
          <NewSubscriptionPanel />
        </ResponsiveDrawerOrInline>
      </NewSubscriptionPanelProvider>
    );
  }

  return (
    <ViewSubscriptionPanelProvider subscriptionId={subscriptionId}>
      <ResponsiveDrawerOrInline
        onClose={onClose}
        title="Subscription details"
        inlineAt="desktop"
      >
        <ViewSubscriptionPanel subscriptionId={subscriptionId} />
      </ResponsiveDrawerOrInline>
    </ViewSubscriptionPanelProvider>
  );
};

export const WebhooksPage = ({
  breadcrumbs,
}: {
  breadcrumbs: Breadcrumb[];
}) => {
  const { subscriptionId: encodedSubscriptionId } = useParams<{
    subscriptionId?: string;
  }>();
  const navigate = useNavigate();
  const { permissions } = usePermissions();

  const subscriptionId = decodeURIComponent(encodedSubscriptionId ?? '');

  const eventTypeState = useEventTypeState();
  const scopeState = useScopeState();
  const hirerState = useHirerState(scopeState.hirerScopedIndicator);

  return (
    <BasePage title="Webhook subscriptions" breadcrumbs={breadcrumbs}>
      <Stack space="medium">
        {permissions.includes('mutate:webhooks') ? (
          <Inline space="none">
            <ButtonLink
              href="/manage/webhooks/new"
              icon={<IconAdd />}
              id="new-webhook-subscription-link"
              variant="soft"
            >
              New subscription
            </ButtonLink>
          </Inline>
        ) : null}

        <Stack space="small">
          <Columns space="medium" collapseBelow="tablet">
            <Column width="content">
              <EventTypePicker {...eventTypeState} align="left" />
            </Column>

            <Column width="content">
              <ScopePicker {...scopeState} />
            </Column>

            <Column width="content">
              <HirerPicker idPrefix="webhookSubscription" {...hirerState} />
            </Column>
          </Columns>

          <Divider />

          <Columns space="xlarge" collapseBelow="desktop">
            <Column width="1/3">
              <SubscriptionListPanel
                eventTypeState={eventTypeState}
                scopeState={scopeState}
                hirerState={hirerState}
              />
            </Column>

            {subscriptionId ? (
              <Column>
                <SubscriptionDetails
                  subscriptionId={subscriptionId}
                  onClose={() => navigate('/manage/webhooks')}
                />
              </Column>
            ) : null}
          </Columns>
        </Stack>
      </Stack>
    </BasePage>
  );
};
